import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import Logo from "../components/site/Logo";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const [detail, setDetail] = useState(null);

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setDetail(data);
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (e) {
        setState("error");
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F0F5F2] px-6" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-3xl border border-[#DCECE3] bg-white p-10 text-center shadow-[0_8px_40px_rgba(11,40,27,0.06)]">
        <div className="flex justify-center">
          <Logo />
        </div>

        {state === "checking" && (
          <div className="mt-8" data-testid="payment-checking">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#75C69F]" />
            <h1 className="mt-5 font-heading text-2xl font-medium text-[#0B281B]">
              Confirming your payment…
            </h1>
            <p className="mt-2 text-[#4A6657]">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "success" && (
          <div className="mt-8" data-testid="payment-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#75C69F]" />
            <h1 className="mt-5 font-heading text-3xl font-medium text-[#0B281B]">
              Payment received!
            </h1>
            <p className="mt-3 text-[#4A6657]">
              Thank you for choosing Sprig Built{detail?.package_name ? ` — ${detail.package_name}` : ""}.
              We'll reach out within one business day to kick things off.
            </p>
            {detail?.amount && (
              <p className="mt-4 inline-block rounded-full bg-[#F0F5F2] px-4 py-1.5 text-sm font-medium text-[#0B281B]">
                Paid ${Number(detail.amount).toFixed(2)} {String(detail.currency || "").toUpperCase()}
              </p>
            )}
          </div>
        )}

        {state === "timeout" && (
          <div className="mt-8" data-testid="payment-timeout">
            <Loader2 className="mx-auto h-12 w-12 text-[#4A6657]" />
            <h1 className="mt-5 font-heading text-2xl font-medium text-[#0B281B]">
              Still processing
            </h1>
            <p className="mt-2 text-[#4A6657]">
              Your payment is taking a little longer than usual. If you completed
              checkout, you'll receive an email confirmation shortly.
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="mt-8" data-testid="payment-error">
            <XCircle className="mx-auto h-12 w-12 text-[#E27B7B]" />
            <h1 className="mt-5 font-heading text-2xl font-medium text-[#0B281B]">
              Something went wrong
            </h1>
            <p className="mt-2 text-[#4A6657]">
              We couldn't confirm this payment. If you were charged, please contact
              us at hello@sprigbuilt.com.
            </p>
          </div>
        )}

        <Link
          to="/"
          data-testid="payment-back-home"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0B281B] px-6 py-3 font-medium text-white transition-colors hover:bg-[#1A3F2C]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </div>
    </div>
  );
}
